<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>Search History </v-card-title>
          <v-card-subtitle>
            {{ getOrg }}
          </v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>

    <!-- filters -->
    <v-row>
      <v-col>
        <v-card elevation="5">
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="2">
                <v-text-field label="Case Name" v-model="caseName" dense clearable />
              </v-col>
              <v-col cols="2">
                <v-text-field label="Case ID" v-model="caseId" dense clearable />
              </v-col>
              <v-col cols="3">
                <v-select dense label="Screened By" v-model="screenedBy" :items="users" item-value="email" :item-text="(item) => {
                    return getTextForUserSelect(item);
                  }
                  " clearable />
              </v-col>
              <!-- screened on -->
              <v-col cols="3">
                <v-menu ref="screeningDateRange" v-model="screeningDateRange" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="dateRangeFormattedComputed" label="Last Screening Date" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense clearable @click:clear="clearScreeningDates"></v-text-field>
                  </template>
                  <v-date-picker v-model="dates" range no-title>
                    <v-spacer />
                    <v-btn text color="primary" @click="screeningDateRange = false">
                      Cancel
                    </v-btn>
                    <v-btn text color="primary" @click="$refs.screeningDateRange.save(dates)">
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>

              <v-spacer />
              <v-col align="right" cols="1">
                <v-btn fab dark x-small color="blue" @click="getHistory()">
                  <v-icon dark>mdi-magnify</v-icon>
                </v-btn>
                <!-- TODO put in refresh button -->
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- table -->
    <v-row dense>
      <v-col>
        <v-data-table 
            :headers="headers" 
            :items="history" 
            :options.sync="options" 
            :server-items-length="totalHistoryRecords" 
            :footer-props="footerProps" 
            disable-sort 
            item-key="id" 
            :loading="loading" 
            no-data-text="No search history"
          >
          <template v-slot:top>
            <v-toolbar flat>
              <v-spacer />
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" dark v-bind="attrs" v-on="on" :loading="buttonLoading">
                    Actions
                    <v-icon dark right> mdi-chevron-down </v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item link @click="onCsvExport()">
                    <v-list-item-icon>
                      <v-icon> mdi-download </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Export History</v-list-item-title>
                      <v-list-item-subtitle>Export CSV based on current search
                        parameters</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-toolbar>
          </template>

          <template v-slot:item.firstScreened="{ item }">
            <span v-if="item.screeningHistory && item.screeningHistory.length > 0">
              {{ formatDateTime(item.screeningHistory[0].screenDate) }}
            </span>
          </template>

          <template v-slot:item.lastScreened="{ item }">
            <span v-if="item.screeningHistory && item.screeningHistory.length > 1">
              {{ formatDateTime(item.screeningHistory[item.screeningHistory.length - 1].screenDate) }}
            </span>
            <span v-else>-</span>
          </template>

          <template v-slot:item.view="{item, index}">
            <v-row dense>
              <v-col>
                <v-btn icon x-small color="green" @click="onViewClick(item, index)" :loading="viewBtnLoading[index]">
                  <v-icon dark>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>

        </v-data-table>
      </v-col>
    </v-row>

    <ViewCase class="position-relative" v-if="selectedCase" :selectedCase="selectedCase" :selectedCaseHistory="selectedCaseHistory" :visible="isViewDialogVisible" :showVatStatus="false" v-on:onCancelled="onViewCaseClose" />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
// import SourceChips from "../components/SourceChips";
import {getCase, getCaseAndHistory} from "../utils/caseUtils"
import ViewCase from "../modals/case/ViewCase";
import {
  formatDateTime,
  formatDate,
  dateRangeFormatted,
} from "../utils/dateUtils";
import { getTextForUserSelect } from "../utils/userUtils";
// import Papa from "papaparse";
export default {
  name: "History",
  components: { ViewCase },
  computed: {
    ...mapGetters(["getOrg", "users"]), // Commenting out users, as it triggers 401 for users who don't have access to /users. Doesn't appear this is used
    dateRangeFormattedComputed: {
      get: function () {
        return this.dateRangeFormatted(this.dates[0], this.dates[1]);
      },
      set: function (newDates) {
        newDates;
        //this.dates = [newDates];
      },
    },
  },
  data() {
    return {
      selectedCase: null,
      isViewDialogVisible: false,
      selectedCaseReport: {},
      searchSummaryVisible: false,
      history: [],
      totalHistoryRecords: 0,
      loading: false,
      buttonLoading: false,
      viewBtnLoading: {},
      options: {},
      footerProps: { "items-per-page-options": [10, 20, 50] },

      // search
      caseName: "",
      caseId: "",
      screenedBy: null,
      assignedTo: null,
      screeningDateRange: false,
      dates: [],
      selected: [],

      headers: [
        { text: "Case", value: "caseName" },
        { text: "Local ID", value: "localId" },
        { text: "First Screened", value: "firstScreened" },
        { text: "Last Screened", value: "lastScreened" },
        {
          text: "View",
          value: "view",
          sortable: false,
          align: "right",
          width: 60,
        }

      ],
    };
  },
  watch: {
    options: {
      handler() {
        this.getHistory();
      },
      deep: true,
    },
  },
  methods: {
    formatDateTime,
    formatDate,
    dateRangeFormatted,
    getTextForUserSelect,
    getCase,
    getCaseAndHistory,
    ...mapActions(["getUsers", "fetchHistory", "setGlobalAlert"]),
    clearScreeningDates() {
      this.dates = [];
    },
    onCsvExport() {
      var request = {};
      // if (this.screenedBy) request.screenedByUserName = this.screenedBy.email;
      // if (this.assignedTo) request.assignedToUserName = this.assignedTo.email;
      // if (this.caseName) request.caseName = this.caseName;
      // if (this.dates[0]) request.screenedAfter = this.dates[0];
      // if (this.dates[1]) request.screenedBefore = this.dates[1];
      // if (this.status) {
      //   request.status = [this.status];
      // }

      if (this.screenedBy) request.screenedBy = this.screenedBy;
      if (this.assignedTo) request.assignedTo = this.assignedTo;
      if (this.caseName) request.caseName = this.caseName;
      if (this.caseId) request.caseId = this.caseId;
      if (this.dates[0]) request.screenedAfter = this.dates[0];
      if (this.dates[1]) request.screenedBefore = this.dates[1];

      //api/reports/csv/history
      let routeData = this.$router.resolve({
        name: "historyCsv",
        query: request,
      });
      const link = document.createElement("a");
      link.href = routeData.href;
      link.click();
    },
    getHistory() {
      this.loading = true;
      var request = { ...this.options };
      if (this.screenedBy) request.screenedBy = this.screenedBy;
      if (this.assignedTo) request.assignedTo = this.assignedTo;
      if (this.caseName) request.caseName = this.caseName;
      if (this.caseId) request.caseId = this.caseId;
      if (this.dates[0]) request.screenedAfter = this.dates[0];
      if (this.dates[1]) request.screenedBefore = this.dates[1];

      axios
        .post("/api/history/", request)
        .then((res) => {
          this.history = res.data.history;
          this.totalHistoryRecords = res.data.totalHistoryRecords;
        })
        .catch(() => {
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertTitle: "Oops.",
            alertMessage: "Error retreiving history. Please try again",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onViewClick(history, index) {
      if(history.caseId){
        this.$set(this.viewBtnLoading, index, true)
        this.getCaseAndHistory(history.caseId)
          .then((res) => {
            this.selectedCase = res.case
            this.selectedCaseHistory = res.history
            this.isViewDialogVisible = true;
          })
          .catch((err) => {
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertMessage: "Error opening case, please try again.",
            });
            console.error(err)
          })
          .finally(() => {
            this.viewBtnLoading = {}
          })
      } else {
        this.selectedCaseHistory = history
        this.selectedCase = { "name" : history.caseName}
        this.isViewDialogVisible = true;
      }
    },
    onViewCaseClose() {
      this.isViewDialogVisible = false;
      this.selectedCase = null; // TODO is this needed if using isVisibile and the component doesn't need created() called?
    },
  },
  // onCsvExport(caseReports) {
  //   this.exportCsv(caseReports);
  // },

  // TODO getStatus not being used
  // getStatus(code) {
  //   if (code == "U") {
  //     return this.$t("modal.caseReport.label.unresolved");
  //   } else if (code == "RN") {
  //     return this.$t("modal.caseReport.label.resolvedNoMatches");
  //   } else if (code == "RP") {
  //     return this.$t("modal.caseReport.label.resolvedProceed");
  //   } else if (code == "RD") {
  //     return this.$t("modal.caseReport.label.resolvedDiscontinue");
  //   } else if (code == "F") {
  //     return this.$t("modal.caseReport.label.flagged");
  //   }
  // },
  // exportCsv(caseReports) {
  //   var csvArray = [];

  //   caseReports.forEach((caseReport) => {
  //     csvArray.push({
  //       "Search Date": this.formatDateTime(caseReport.searchDate),
  //       ID: caseReport.id,
  //       "Case Name": caseReport.searchCase.name,
  //       Type: caseReport.searchCase.type,
  //       "Case ID": caseReport.searchCase.localId,
  //       "Case DOB": this.formatDate(caseReport.searchCase.dob),
  //       "Case Citizenship": caseReport.searchCase.citizenship,
  //       Nationality: caseReport.searchCase.nationality,
  //       "Case Phone Number": caseReport.searchCase.phoneNumber,
  //       "Case Gender": caseReport.searchCase.gender,
  //       "Case EIN/TIN": caseReport.searchCase.taxId,
  //       "Case Passport Number": caseReport.searchCase.passport
  //         ? caseReport.searchCase.passport.passportNumber
  //         : null,
  //       "Case Passport Country": caseReport.searchCase.passport
  //         ? caseReport.searchCase.passport.passportCountry
  //         : null,
  //       "Case Address 1": caseReport.searchCase.address
  //         ? caseReport.searchCase.address.address1
  //         : null,
  //       "Case Address 2": caseReport.searchCase.address
  //         ? caseReport.searchCase.address.address2
  //         : null,
  //       "Case Address 3": caseReport.searchCase.address
  //         ? caseReport.searchCase.address.address3
  //         : null,
  //       "Case Address 4": caseReport.searchCase.address
  //         ? caseReport.searchCase.address.address4
  //         : null,
  //       "Case City": caseReport.searchCase.address
  //         ? caseReport.searchCase.address.city
  //         : null,
  //       "Case State or Province": caseReport.searchCase.address
  //         ? caseReport.searchCase.address.stateOrProvince
  //         : null,
  //       "Case Postal Code": caseReport.searchCase.address
  //         ? caseReport.searchCase.address.postalCode
  //         : null,
  //       "Case Country": caseReport.searchCase.address
  //         ? caseReport.searchCase.address.country
  //         : null,
  //       Matches: caseReport.matches.length,
  //       Status: this.getStatus(caseReport.status),
  //       "Screened By": caseReport.screenedByUserName,
  //       "Assigned To": caseReport.assignedToUserName,
  //     });

  //     // if(caseReport.caseAttributes && caseReport.caseAttributes.size > 0){
  //     //   caseReport.caseAttributes.forEach((value, key) => {
  //     //     csvArray.push({key : value})
  //     //   });
  //     // }
  //   });

  //   var csv = Papa.unparse(csvArray);
  //   const blob = new Blob([csv]);
  //   const a = document.createElement("a");
  //   a.href = URL.createObjectURL(blob, { type: "text/csv;charset=utf-8;" });
  //   a.download = "veriscan-export-" + this.formatDate(new Date()) + ".csv";
  //   document.body.appendChild(a);
  //   a.click();
  //   document.body.removeChild(a);
  // },
  mounted() {
    //this.getHistory(); // This was causing double calls to history API. 
  },
};
</script>

<style></style>